import { Container, Graphics, Polygon, Text } from 'pixi.js';
import type { Zone, ZoneExit, ZoneObject } from '@bratva/content';
import { createRng, type SpawnedCar } from '@bratva/rules';
import { TILE_DIAMOND, TILE_H, TILE_W, depth, gridToCenter, gridToScreen } from './iso.js';

/**
 * Заглушки вместо спрайтов: коробки и ромбы рисуются кодом, пока пайплайн
 * Blender → спрайты не отдал первые атласы. Форма и габариты уже настоящие,
 * поэтому подмена ассетов не поедет по композиции.
 */

interface KindStyle {
  height: number;
  top: number;
  label?: boolean;
}

const KINDS: Record<string, KindStyle> = {
  panelka: { height: 132, top: 0x8a8578 },
  podezd: { height: 44, top: 0x6f6a60, label: true },
  laryok: { height: 48, top: 0xb08a3c, label: true },
  garazhi: { height: 40, top: 0x7c5f45, label: true },
  priemka: { height: 38, top: 0x5f6b74, label: true },
  lavochka: { height: 12, top: 0x7a5c3a },
  musorka: { height: 20, top: 0x4f5a52 },
  zabor: { height: 26, top: 0x6b5f4d },
  topol: { height: 0, top: 0x4e6b3a },
  sharaga: { height: 96, top: 0x9a8f72, label: true },
  shinomontazh: { height: 36, top: 0x5a6470, label: true },
  kachalka: { height: 34, top: 0x7b5e3c, label: true },
  shkolota: { height: 18, top: 0x51606b, label: true },
  videosalon: { height: 34, top: 0xa05a3c, label: true },
  katran: { height: 44, top: 0x4b5a66, label: true },
  baryga: { height: 30, top: 0x6a5a46, label: true },
  diskach: { height: 40, top: 0x7a4b7e, label: true },
  zadrot: { height: 22, top: 0x4a5f74, label: true },
  kolshchik: { height: 22, top: 0x6b4a4a, label: true },
  smotryashiy: { height: 24, top: 0x3f4a5c, label: true },
  fraer: { height: 22, top: 0x6e6448, label: true },
  gopota: { height: 20, top: 0x515c4a, label: true },
};

const GROUND: Record<string, number> = {
  '.': 0x6d6e73, // асфальт
  ',': 0x86745c, // тропинка
  g: 0x66754a, // трава
};

function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

function parseTint(tint: string | undefined, fallback: number): number {
  if (!tint) return fallback;
  const parsed = Number.parseInt(tint.replace('#', ''), 16);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/** Вся земля зоны — одна Graphics: тайлы не меняются, перерисовывать нечего. */
export function drawGround(zone: Zone): Graphics {
  const g = new Graphics();
  const rng = createRng(`${zone.id}:terrain`);

  for (let y = 0; y < zone.size.height; y++) {
    const row = zone.terrain[y] ?? '';
    for (let x = 0; x < zone.size.width; x++) {
      const base = GROUND[row[x] ?? '.'] ?? GROUND['.']!;
      // Лёгкий разнобой оттенка, чтобы асфальт не выглядел заливкой.
      const color = shade(base, 0.94 + rng.next() * 0.12);
      const p = gridToScreen(x, y);
      const pts: number[] = [];
      for (let i = 0; i < TILE_DIAMOND.length; i += 2) {
        pts.push(p.x + TILE_DIAMOND[i]!, p.y + TILE_DIAMOND[i + 1]!);
      }
      g.poly(pts).fill({ color });
    }
  }

  // Сетка поверх земли — слабая, чтобы читались расстояния в шагах.
  for (let y = 0; y <= zone.size.height; y++) {
    const a = gridToScreen(0, y);
    const b = gridToScreen(zone.size.width, y);
    g.moveTo(a.x, a.y).lineTo(b.x, b.y);
  }
  for (let x = 0; x <= zone.size.width; x++) {
    const a = gridToScreen(x, 0);
    const b = gridToScreen(x, zone.size.height);
    g.moveTo(a.x, a.y).lineTo(b.x, b.y);
  }
  g.stroke({ color: 0x000000, alpha: 0.07, width: 1 });

  return g;
}

/** Коробка на футпринте объекта: две видимые стены и крыша. */
export function drawObject(obj: ZoneObject): Container {
  const style = KINDS[obj.kind] ?? { height: 30, top: 0x777777 };
  const container = new Container();
  container.zIndex = depth(obj.x + obj.w - 1, obj.y + obj.h - 1);

  if (obj.kind === 'topol') {
    container.addChild(drawTree(obj));
    return container;
  }

  const top = parseTint(obj.tint, style.top);
  const g = new Graphics();

  const n = gridToScreen(obj.x, obj.y);
  const e = gridToScreen(obj.x + obj.w, obj.y);
  const s = gridToScreen(obj.x + obj.w, obj.y + obj.h);
  const w = gridToScreen(obj.x, obj.y + obj.h);
  const z = style.height;

  // Левая стена (запад — юг), правая (юг — восток), потом крыша.
  g.poly([w.x, w.y, s.x, s.y, s.x, s.y - z, w.x, w.y - z]).fill({ color: shade(top, 0.62) });
  g.poly([s.x, s.y, e.x, e.y, e.x, e.y - z, s.x, s.y - z]).fill({ color: shade(top, 0.78) });
  g.poly([n.x, n.y - z, e.x, e.y - z, s.x, s.y - z, w.x, w.y - z]).fill({ color: top });
  g.stroke({ color: 0x000000, alpha: 0.25, width: 1 });

  container.addChild(g);
  return container;
}

function drawTree(obj: ZoneObject): Graphics {
  const g = new Graphics();
  const base = gridToScreen(obj.x, obj.y);
  const cx = base.x;
  const cy = base.y + TILE_H;
  g.rect(cx - 3, cy - 46, 6, 46).fill({ color: 0x5b4632 });
  g.ellipse(cx, cy - 62, 22, 30).fill({ color: 0x4e6b3a });
  g.ellipse(cx - 8, cy - 50, 14, 18).fill({ color: 0x577440 });
  return g;
}

/** Пацан: пока капсула, но габариты и точка опоры уже как у будущего спрайта. */
export function drawPlayer(): Container {
  const container = new Container();
  const g = new Graphics();
  g.ellipse(0, 0, 14, 7).fill({ color: 0x000000, alpha: 0.28 });
  g.roundRect(-8, -44, 16, 34, 7).fill({ color: 0xa6123c });
  g.circle(0, -50, 8).fill({ color: 0xd8b98f });
  g.rect(-8, -30, 16, 3).fill({ color: 0xf2f2f2 });
  g.rect(-8, -24, 16, 3).fill({ color: 0xf2f2f2 });
  container.addChild(g);
  return container;
}

/** Мусор: та же капсула, но в форме и с кругом внимания под ногами. */
export function drawPatrol(attention: number, hunting: boolean, sprite?: Container | null): Container {
  const container = new Container();

  const zone = new Graphics();
  zone
    .ellipse(0, 0, attention * (TILE_W / 2), attention * (TILE_H / 2))
    .fill({ color: hunting ? 0xe4486a : 0x6fa0d8, alpha: hunting ? 0.14 : 0.09 });
  zone
    .ellipse(0, 0, attention * (TILE_W / 2), attention * (TILE_H / 2))
    .stroke({ color: hunting ? 0xe4486a : 0x6fa0d8, alpha: 0.5, width: 1 });
  container.addChild(zone);

  if (sprite) {
    container.addChild(drawShadow(), sprite);
    return container;
  }

  const g = new Graphics();
  g.ellipse(0, 0, 13, 6).fill({ color: 0x000000, alpha: 0.28 });
  g.roundRect(-8, -42, 16, 32, 6).fill({ color: 0x33507a });
  g.circle(0, -48, 8).fill({ color: 0xd8b98f });
  // Фуражка: по ней патруль читается издалека даже в заглушках.
  g.roundRect(-10, -56, 20, 6, 2).fill({ color: 0x1f2f4a });
  container.addChild(g);

  return container;
}

/** Тень под спрайтом: без неё фигура висит над асфальтом. */
export function drawShadow(rx = 13, ry = 6): Graphics {
  return new Graphics().ellipse(0, 0, rx, ry).fill({ color: 0x000000, alpha: 0.3 });
}

/** Подсветка клетки под курсором и точки маршрута. */
export function drawTileMarker(color: number, alpha: number): Graphics {
  const g = new Graphics();
  const pts: number[] = [];
  for (let i = 0; i < TILE_DIAMOND.length; i += 2) {
    pts.push(TILE_DIAMOND[i]! * 0.9, TILE_DIAMOND[i + 1]! * 0.9);
  }
  g.poly(pts).fill({ color, alpha });
  return g;
}

/** Подпись объекта или выхода — читается поверх сцены, масштаб не меняется. */
export function drawLabel(text: string, gx: number, gy: number, color = 0xe8e9ed, zIndex?: number): Text {
  const label = new Text({
    text,
    style: {
      fontFamily: 'Golos Text, Segoe UI, system-ui, sans-serif',
      fontSize: 13,
      fill: color,
      stroke: { color: 0x0f1013, width: 4, join: 'round' },
    },
  });
  const center = gridToCenter(gx, gy);
  label.anchor.set(0.5, 1);
  label.position.set(center.x, center.y - 6);
  label.zIndex = zIndex ?? depth(gx, gy) + 4;
  return label;
}

/**
 * Клетка-выход: разметка лежит на земле и честно уходит за дома,
 * а подпись к ней (см. LABEL_LAYER) всегда поверх — иначе выход за панелькой
 * невозможно найти.
 */
export function drawExit(exit: ZoneExit): Container {
  const container = new Container();
  container.zIndex = depth(exit.x, exit.y) - 2;

  const g = new Graphics();
  for (let y = exit.y; y < exit.y + exit.h; y++) {
    for (let x = exit.x; x < exit.x + exit.w; x++) {
      const center = gridToCenter(x, y);
      const pts: number[] = [];
      for (let i = 0; i < TILE_DIAMOND.length; i += 2) {
        pts.push(center.x + TILE_DIAMOND[i]!, center.y + TILE_DIAMOND[i + 1]! - TILE_H / 2);
      }
      g.poly(pts).fill({ color: 0xe4486a, alpha: 0.2 });
    }
  }

  // Контур по внешней рамке полосы, а не вокруг каждой клетки: так читается
  // как дорога из двора, а не как россыпь меток.
  const n = gridToScreen(exit.x, exit.y);
  const e = gridToScreen(exit.x + exit.w, exit.y);
  const s = gridToScreen(exit.x + exit.w, exit.y + exit.h);
  const w = gridToScreen(exit.x, exit.y + exit.h);
  g.poly([n.x, n.y, e.x, e.y, s.x, s.y, w.x, w.y]).stroke({ color: 0xe4486a, alpha: 0.85, width: 2 });

  container.addChild(g);
  return container;
}

/**
 * Силуэт коробки в изометрии: по нему ловятся клики.
 * Без него курсор попадает в клетку земли под крышей, а не в само здание.
 */
function silhouette(x: number, y: number, w: number, h: number, z: number): Polygon {
  const n = gridToScreen(x, y);
  const e = gridToScreen(x + w, y);
  const s = gridToScreen(x + w, y + h);
  const west = gridToScreen(x, y + h);
  return new Polygon([
    n.x, n.y - z,
    e.x, e.y - z,
    e.x, e.y,
    s.x, s.y,
    west.x, west.y,
    west.x, west.y - z,
  ]);
}

export function objectHitArea(obj: ZoneObject): Polygon {
  const style = KINDS[obj.kind];
  // У тополя ствол с кроной, а не коробка: ловим клик по клетке с запасом вверх.
  const height = obj.kind === 'topol' ? 80 : (style?.height ?? 30);
  return silhouette(obj.x, obj.y, obj.w, obj.h, height);
}

export function carHitArea(car: SpawnedCar): Polygon {
  return silhouette(car.x, car.y, 1, 1, 22);
}

/** Подписи-указатели рисуются поверх всей сцены. */
export const LABEL_LAYER = 1_000_000;

export function drawExitLabel(exit: ZoneExit): Text {
  const cx = exit.x + (exit.w - 1) / 2;
  const cy = exit.y + (exit.h - 1) / 2;
  return drawLabel(`${exit.label} · ${exit.minutes} мин`, cx, cy, 0xf2b8c6, LABEL_LAYER + depth(exit.x, exit.y));
}

/** Подпись объекта поднимается над собственным зданием, но не над всей сценой. */
export function drawObjectLabel(obj: ZoneObject): Text {
  const gx = obj.x + (obj.w - 1) / 2;
  const gy = obj.y + obj.h - 1;
  return drawLabel(obj.label ?? '', gx, gy, 0xe8e9ed, depth(obj.x + obj.w - 1, obj.y + obj.h - 1) + 8);
}

/** Тачка на размеченном месте: в M2 по ней будут кликать ради магнитолы. */
export function drawCar(car: SpawnedCar): Container {
  const container = new Container();
  container.zIndex = depth(car.x, car.y);

  const body: Record<string, number> = {
    kopeyka: 0x9aa39b,
    zubilo: 0xb2a06a,
    moskvich: 0x7d8f9c,
    volga: 0x2f3742,
    bumer: 0x1f2228,
  };
  const color = body[car.model] ?? 0x8a8a8a;
  const center = gridToCenter(car.x, car.y);
  const g = new Graphics();

  // Кузов — коробка на клетку, поэтому габариты совпадут с будущим спрайтом.
  const n = gridToScreen(car.x, car.y);
  const e = gridToScreen(car.x + 1, car.y);
  const s = gridToScreen(car.x + 1, car.y + 1);
  const w = gridToScreen(car.x, car.y + 1);
  const z = 16;

  g.poly([w.x, w.y, s.x, s.y, s.x, s.y - z, w.x, w.y - z]).fill({ color: shade(color, 0.6) });
  g.poly([s.x, s.y, e.x, e.y, e.x, e.y - z, s.x, s.y - z]).fill({ color: shade(color, 0.78) });
  g.poly([n.x, n.y - z, e.x, e.y - z, s.x, s.y - z, w.x, w.y - z]).fill({ color });
  g.ellipse(center.x, center.y - z - 6, 12, 6).fill({ color: shade(color, 1.25), alpha: 0.9 });
  container.addChild(g);

  return container;
}

export const TILE_SIZE = { width: TILE_W, height: TILE_H };
